import React, { useRef, useEffect, useState } from "react";
import esriConfig from "@arcgis/core/config";
import * as urlUtils from "@arcgis/core/core/urlUtils";
import esriRequest from "@arcgis/core/request";
import Map from "@arcgis/core/Map";
import MapView from "@arcgis/core/views/MapView";
import FeatureLayer from "@arcgis/core/layers/FeatureLayer";
import Graphic from "@arcgis/core/Graphic";

const FEATURE_SERVER = "https://gis.economy.ge/server/rest/services/TCSA/TCSAGraphics/FeatureServer";
const PROXY_BASE = "https://tcsa.gov.ge";

// Known permit-type → layer-id (same as useFeatureExists.js); used as fast path.
const LAYER_MAP = {
    41: 10, 42: 11, 43: 12, 44: 13, 45: 14, 46: 15,
    47: 16, 48: 17, 49: 18, 50: 19, 51: 20, 52: 21, 53: 22,
    54: 23, 55: 24, 56: 25, 57: 26, 58: 27, 59: 28,
    11: 7, 14: 7, 18: 7, 167: 7, 192: 7,
    4: 3, 6: 3, 7: 3, 8: 3, 9: 3, 158: 3, 159: 3, 160: 3,
    161: 3, 162: 3, 163: 3, 164: 3, 171: 3, 212: 3, 213: 3,
    214: 3, 215: 3,
    39: 0,
};

// Query a FeatureServer layer for the first matching feature; return geometry or null.
const queryLayerFeature = async (layerId, where) => {
    try {
        const layer = new FeatureLayer({ url: `${FEATURE_SERVER}/${layerId}` });
        await layer.load();
        const result = await layer.queryFeatures({
            where,
            outFields: ["*"],
            returnGeometry: true,
            num: 1,
        });
        return result.features?.[0]?.geometry ?? null;
    } catch (_) {
        return null;
    }
};

export const InteractiveMapSaagento = ({ caseData }) => {
    const mapDiv = useRef(null);
    const viewRef = useRef(null);
    const [noGraphic, setNoGraphic] = useState(false);

    // Create map + view once on mount
    useEffect(() => {
        if (!mapDiv.current) return;

        esriConfig.request.corsDetection = false;
        esriConfig.request.proxyUrl = `${PROXY_BASE}/DotNet/proxy.ashx`;
        urlUtils.addProxyRule({
            urlPrefix: "https://gis.economy.ge",
            proxyUrl: `${PROXY_BASE}/DotNet/proxy.ashx`,
        });

        const map = new Map({ basemap: "hybrid" });
        const view = new MapView({
            container: mapDiv.current,
            map,
            center: [43.367997, 42.095516],
            zoom: 8,
            ui: { components: ["zoom"] },
        });
        viewRef.current = view;

        return () => {
            view.destroy();
            viewRef.current = null;
        };
    }, []);

    // Zoom to and highlight the case's feature once caseData is available
    useEffect(() => {
        if (!caseData?.caseNumber) return;

        let cancelled = false;

        const doZoom = async () => {
            // Wait for view to be created by the other effect
            let attempts = 0;
            while (!viewRef.current && attempts < 20) {
                await new Promise(r => setTimeout(r, 100));
                attempts++;
            }
            const view = viewRef.current;
            if (!view || cancelled) return;
            await view.when();

            const caseNumber = caseData.caseNumber;
            const whereExact = `gancxadebis_nomeri='${caseNumber}'`;
            const whereLike  = `gancxadebis_nomeri LIKE '%${caseNumber}%'`;

            let geom = null;

            // ── Fast path: try permit-type mapped layer first ──
            const knownLayerId = LAYER_MAP[caseData.permitTypeId];
            if (knownLayerId !== undefined) {
                geom = await queryLayerFeature(knownLayerId, whereExact);
                if (!geom) geom = await queryLayerFeature(knownLayerId, whereLike);
            }

            // ── Fallback: search all layers from server metadata ──
            if (!geom) {
                try {
                    const meta = await esriRequest(FEATURE_SERVER, {
                        query: { f: "json" },
                        responseType: "json",
                    });
                    const allIds = (meta.data?.layers ?? []).map(l => l.id);
                    const remaining = knownLayerId !== undefined
                        ? allIds.filter(id => id !== knownLayerId)
                        : allIds;

                    const exactResults = await Promise.all(
                        remaining.map(id => queryLayerFeature(id, whereExact))
                    );
                    geom = exactResults.find(g => g !== null) ?? null;

                    if (!geom) {
                        const likeResults = await Promise.all(
                            remaining.map(id => queryLayerFeature(id, whereLike))
                        );
                        geom = likeResults.find(g => g !== null) ?? null;
                    }
                } catch (e) {
                    console.warn("InteractiveMapSaagento fallback error:", e);
                }
            }

            if (cancelled) return;

            if (!geom) {
                setNoGraphic(true);
                return;
            }

            // Draw highlight graphic (same style as main map search selection)
            view.graphics.removeAll();
            let symbol;
            if (geom.type === "polygon") {
                symbol = { type: "simple-fill", color: [0, 121, 194, 0.15],
                    outline: { color: [0, 121, 194], width: 3 } };
            } else if (geom.type === "polyline") {
                symbol = { type: "simple-line", color: [0, 121, 194], width: 3 };
            } else {
                symbol = { type: "simple-marker", color: [0, 121, 194], size: 14,
                    outline: { color: [255, 255, 255], width: 2 } };
            }
            view.graphics.add(new Graphic({ geometry: geom, symbol }));

            // Zoom — extent for polygon/polyline, zoom-18 for points
            const goTarget = (geom.type === "polygon" || geom.type === "polyline")
                ? { target: geom.extent?.expand(1.5) }
                : { target: geom, zoom: 18 };
            view.goTo(goTarget).catch(() => {});
            setNoGraphic(false);
        };

        doZoom();
        return () => { cancelled = true; };
    }, [caseData?.caseNumber, caseData?.permitTypeId]);

    return (
        <div style={{ position: "relative", width: "100%", height: "100%" }}>
            <div ref={mapDiv} style={{ width: "100%", height: "100%" }} />
            {noGraphic && (
                <div style={{
                    position: "absolute", inset: 0,
                    display: "flex", alignItems: "center", justifyContent: "center",
                    background: "rgba(0, 0, 0, 0.87)", fontSize: 14,fontWeight: "bold", color: "#555",
                }}>
                    ობიექტს არ აქვს მიბმული გრაფიკა
                </div>
            )}
        </div>
    );
};

export default InteractiveMapSaagento;
